import React from 'react';

import { AxiosError, AxiosResponse } from 'axios';
import { Grid, TextField, Chip } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';

import MediaService from '../../../../Services/MediaService';
import { MediaModel } from '../../../../Models/MediaModel';
import VehicleService from '../../../../Services/VehicleService';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { theme } from '../../../../theme';
import { HomeContext } from '../../../Home/HomeContext';
import { ClientContext } from '../../ClientContext';
import { VehicleModel } from '../../../../Models/VehicleModel';
import { EditAction } from '../../../../shared/enum/EditAction';
import { IShowDialogProps } from '../../components/ClientPages';

const useStyles = makeStyles(() => createStyles({
    itens: {
        width: '100%',
        color: 'primary'
    },
}));

export function ClientVehicleModal({showDialog, setShowDialog}: IShowDialogProps) {
    const classes = useStyles();

    const homeContext = React.useContext(HomeContext);   
    const clientContext = React.useContext(ClientContext);   

    const [vehicleList, setVehicleList] = React.useState<VehicleModel[]>([]);
    const [vehicleSelected, setVehicleSelected] = React.useState<VehicleModel[]>([]);
    const [clientsVehicle, setClientsVehicle] = React.useState<VehicleModel[]>([]);

    const [mediaList, setMediaList] = React.useState<MediaModel[]>([]);
    const [mediaSelect, setMediaSelect] = React.useState<MediaModel | null>(null);

    const [vehicleNotDefinedError, setVehicleNotDefinedError] = React.useState<boolean>(false);
    const [mediaNotDefinedError, setMediaNotDefinedError] = React.useState<boolean>(false);
    
    React.useEffect(() => {
        if (mediaList.length === 0)
            fetchMedia();
    }, []);

    
    React.useEffect(() => {
        if (mediaSelect && mediaSelect.id > 0) {
            homeContext.setOpenLoadingDialog(true);
            VehicleService
                .getVehicleByMediaClient(mediaSelect.id)
                .then((res: AxiosResponse) => {
                    if (res.data)
                        setVehicleList(res.data);
                })
                .catch((error: AxiosError) => {
                    setVehicleList([]);
                    homeContext.showError('Ops.. ocorreu um erro e não foi possível realizar a busca.' +
                        'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                })
                .finally(() => homeContext.setOpenLoadingDialog(false));

        }
    } , [mediaSelect]);

    
    React.useEffect(() => {
        if (vehicleList && vehicleList.length > 0 && vehicleSelected) {
            const array: VehicleModel[] = [];
            vehicleSelected?.forEach(vehicle => {
                if (vehicle) {
                    const foundVehicle = vehicleList.find(v => v.id == vehicle.id);
                    if (foundVehicle) {                        
                        array.push(foundVehicle);
                    }
                }
            });
            setClientsVehicle(array);
        }
    }, [vehicleList, vehicleSelected]);

    const fetchMedia = () => {
        homeContext.setOpenLoadingDialog(true);

        MediaService.mediaService()
            .getAll()
            .then((res: AxiosResponse) => { setMediaList(res.data); })
            .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Mídia. <br/>', error); })
            .finally(() => homeContext.setOpenLoadingDialog(false));
    };

    const hasError = () => {
        const mediaError = !mediaSelect;
        setMediaNotDefinedError(mediaError);

        const vehicleError = !(vehicleSelected && vehicleSelected.length > 0);
        setVehicleNotDefinedError(vehicleError);

        return mediaError || vehicleError;
    };

    const clearModal = () => {
        setShowDialog(false); 
        setClientsVehicle([]);
        setVehicleSelected([]);
        setMediaSelect(null);
        setMediaNotDefinedError(false);
        setVehicleNotDefinedError(false);
    };

    const handleAddVehicle = () => {
        if(!hasError()) { 
            let vehicles = [...clientContext.vehicles];

            if(vehicles && vehicles.length > 0) { 
                const array: VehicleModel[] = [];
                clientsVehicle?.forEach((vehicle : VehicleModel) => {
                    if (vehicle) {
                        const foundVehicle = vehicles.find(v => v.id == vehicle.id);
                        if (!foundVehicle)
                            array.push({...vehicle, editAction: EditAction.INSERT});                        
                    }
                });

                vehicles = vehicles.concat(array).sort((a,b) => 
                    ('' + a.nomenclature).localeCompare('' + b.nomenclature));
            }
            else
            {
                const vehicleArray = JSON.parse(JSON.stringify(clientsVehicle));
                
                vehicleArray.forEach((v:VehicleModel) => v.editAction = EditAction.INSERT);
                vehicles = vehicleArray;
            }
            
            clientContext.setVehicles(vehicles);

            clearModal();
        }
    };
    
    const getVehicleModal = () => { 
        return (
            <Grid container spacing={3}>
                { getModalMediaInput() }
                { getModalVehicleInput() }
            </Grid>
        );
    };

    const getModalMediaInput = () => {
        return (
            <Grid item xs={12}  md={6}>
                <Autocomplete
                    id='client-media'
                    className={classes.itens}
                    value={mediaSelect}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    options={mediaList}
                    getOptionLabel={(option: MediaModel) => option.name}
                    onChange={(_, value: MediaModel | null) => {
                        if (vehicleSelected && vehicleSelected.length > 0) {
                            homeContext.showWarning('Salve os veículos do tipo de mídia escolhidos antes de realizar um novo filtro, ' + 
                                'ou apague os veículos selecionados.');
                        } else if (value)
                            setMediaSelect(value);
                    }}
                    renderInput={(params) =>
                        <TextField {...params}
                            label="Tipo de mídia"
                            variant="outlined"
                            error={mediaNotDefinedError && !mediaSelect}
                            helperText={mediaNotDefinedError && !mediaSelect ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };

    const getModalVehicleInput = () => {
        return (
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    id='client-vehicles'
                    className={classes.itens}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    value={ clientsVehicle }
                    disabled={!mediaSelect}
                    onChange={(_, value: VehicleModel[]) => { setVehicleSelected(value); }}
                    options={vehicleList ? vehicleList.filter(vehicle => !clientContext.vehicles.some(cv => cv.id === vehicle.id)) : []}
                    getOptionLabel={(option: VehicleModel) => option.nomenclature || ''}
                    filterSelectedOptions
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                label={option.name}
                                {...getTagProps({ index })} />
                        ))
                    }
                    renderInput={(params) =>
                        <TextField {...params}
                            label="Veículos"
                            variant="outlined"
                            error={vehicleNotDefinedError && !(clientsVehicle.length > 0)}
                            helperText={vehicleNotDefinedError && !(clientsVehicle.length > 0) ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };
    
    return (
        <BasicConfirmDialog
            title="Adicionar veículo"
            description="Para adicionar um veículo, selecione o tipo de mídia e busque o veículo desejado"
            textFirstButton= 'Descartar'
            handleFirstButton={clearModal}
            textSecondButton= 'Salvar'
            handleSecondButton={handleAddVehicle}
            open={showDialog}
            children={ getVehicleModal() }
            cssProperties={{ 
                minWidth: '700px',
                maxWidth: '700px',
                colorFirstButton: theme.palette.primary.main,
                justifyButtons: 'flex-end',
                titleVariant: 'h4',
                descriptionVariant: 'body1'
            }}/>
    );
}